import { FC, ReactElement } from 'react';
import styled from 'styled-components';

import { Button } from '#/components/Button/Button';
import { PickerItemBaseProps } from '#/components/Picker/PickerItem';
import { ButtonVariant, CONTENT_MAX_WIDTH_PX } from '#/service/constants';
import { remify } from '#/service/utils';
import { tb } from '#/store/translation/translation.store';

import { ProductPickerItem } from '../PlanPurchaseFlow/PickProductStep/getPickerItems';
import { useProductPicker } from './ProductPicker.hook';
import { ProductSlider } from './ProductSlider';
import { ProductTab, ProductTabs } from './ProductTabs';

const ContentContainer = styled.div`
  width: 100%;
  max-width: 100%;
`;

const ActionContainer = styled.div`
  width: 100%;
  margin-bottom: ${remify(24)};
  max-width: ${remify(CONTENT_MAX_WIDTH_PX)};
  margin-inline: auto;
`;

export interface ProductPickerProps {
  /** items to choose from with the picker */
  items: ProductPickerItem[];
  /** handler for what should happen after picking an item */
  onContinue: (id: string) => void;
  /** handler for triggering when user clicks cancel in component*/
  onCancel?: () => void;
  /** translation key for cancel button */
  cancelButtonTranslationKey?: string;
  /** custom renderer for item content - if not provided, uses default ProductCard */
  renderItem?: (
    item: PickerItemBaseProps,
    onContinue: (id: string) => void,
    autoFocus: boolean | undefined
  ) => ReactElement;
  /** explicit tabs for filtering with id and translated label */
  availableTabs?: ProductTab[];
}

/** ProductPicker is a replacement for Picker that shows products in a slider on desktop and list on mobile */
export const ProductPicker: FC<ProductPickerProps> = ({
  items,
  onContinue,
  onCancel,
  cancelButtonTranslationKey = 'button.cancel',
  renderItem,
  availableTabs = [],
}) => {
  const { activeTab, setActiveTab, filteredItems } = useProductPicker({
    items,
    availableTabs,
  });

  return (
    <ContentContainer>
      <ProductTabs
        activeTab={activeTab}
        availableTabs={availableTabs}
        onTabChange={setActiveTab}
      />

      <ProductSlider
        items={filteredItems}
        onContinue={onContinue}
        renderItem={renderItem}
      />

      {onCancel && (
        <ActionContainer>
          <Button
            isFullWidth
            onClick={onCancel}
            variant={ButtonVariant.Secondary}
          >
            {tb(cancelButtonTranslationKey)}
          </Button>
        </ActionContainer>
      )}
    </ContentContainer>
  );
};
